<template>
  <div>
    <a-page-header :title="`步骤管理 - ${schemeName || 'AI调用流程'}`" @back="handleBack">
      <template #extra>
        <a-button type="primary" @click="handleCreateStep">
          <template #icon><PlusOutlined /></template>
          新建步骤
        </a-button>
        <a-button @click="handleCreateWorker" style="margin-left: 8px">
          <template #icon><PlusOutlined /></template>
          新建Worker
        </a-button>
        <a-button @click="handleViewOutputParams" style="margin-left: 8px">
          <template #icon><UnorderedListOutlined /></template>
          查看出参列表
        </a-button>
      </template>
    </a-page-header>

    <a-card style="margin-top: 16px">
      <a-table
        :columns="columns"
        :data-source="dataSource"
        :loading="loading"
        row-key="id"
        :pagination="false"
        :expanded-row-keys="expandedRowKeys"
        :expandIcon="() => null"
        :scroll="{ x: screenWidth < breakpoints.tablet ? 800 : 'max-content' }"
        @expand="handleExpand"
      >
        <template #expandedRowRender="{ record }">
          <div v-if="record.level === 2" style="padding: 16px; background: #fafafa;">
            <div style="margin-bottom: 12px; font-weight: 500; color: #333;">
              Worker: {{ record.step_name }} <span style="color: #999; font-size: 12px;">(ID: {{ record.id }})</span>
            </div>
            <a-collapse v-model:activeKey="expandedWorkers[record.id]" :bordered="false">
              <a-collapse-panel key="knowledge">
                <template #header>
                  <div style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
                    <span>已关联的知识 ({{ (workerKnowledgeMap[record.id] || []).length }})</span>
                    <a-space style="margin-left: auto;" @click.stop>
                      <a-button type="link" size="small" @click.stop="handleAssociateKnowledge(record)">关联知识</a-button>
                      <a-button type="link" size="small" @click.stop="handleSetPromptTemplate(record)">关联提示词结构</a-button>
                      <a-button type="link" size="small" @click.stop="handlePreviewTemplate(record)">预览提示词结构</a-button>
                    </a-space>
                  </div>
                </template>
                <div>
                  <!-- 第一行：提示词结构 -->
                  <div v-if="workerPromptTemplateMap[record.id]" style="padding: 8px 12px; margin-bottom: 8px; background: #e6f7ff; border-radius: 4px; border: 1px solid #91d5ff; display: flex; align-items: center;">
                    <span style="color: #999; margin-right: 12px; min-width: 40px;">0.</span>
                    <a-tag color="red" style="margin-right: 12px;">
                      [提示词结构]
                    </a-tag>
                    <span style="flex: 1; font-weight: 500;">{{ workerPromptTemplateMap[record.id].title }}</span>
                    <a-space>
                      <a-button type="link" size="small" @click="handleEditKnowledge(workerPromptTemplateMap[record.id].id)">查看</a-button>
                    </a-space>
                  </div>
                  <!-- 关联的知识列表 -->
                  <div v-if="workerKnowledgeMap[record.id] && workerKnowledgeMap[record.id].length > 0">
                    <div
                      v-for="(knowledge, index) in workerKnowledgeMap[record.id]"
                      :key="knowledge.id"
                      style="padding: 8px 12px; margin-bottom: 8px; background: white; border-radius: 4px; border: 1px solid #e8e8e8; display: flex; align-items: center;"
                    >
                      <span style="color: #999; margin-right: 12px; min-width: 40px;">{{ index + 1 }}.</span>
                      <a-tag :color="getKnowledgeTypeColor(knowledge.type_code)" style="margin-right: 12px;">
                        [{{ getKnowledgeTypeName(knowledge.type_code) }}]
                      </a-tag>
                      <span style="flex: 1; font-weight: 500;">{{ knowledge.title }}</span>
                      <a-space>
                        <a-button type="link" size="small" @click="handleEditKnowledge(knowledge.id)">查看</a-button>
                      </a-space>
                    </div>
                  </div>
                  <div v-if="(!workerKnowledgeMap[record.id] || workerKnowledgeMap[record.id].length === 0) && !workerPromptTemplateMap[record.id]" style="color: #999; text-align: center; padding: 20px;">
                    暂无关联知识
                  </div>
                </div>
              </a-collapse-panel>
            </a-collapse>
          </div>
        </template>
        <template #headerCell="{ column }">
          <a-button 
            v-if="column.key === 'expand'"
            type="text" 
            size="small" 
            @click="handleToggleExpandAll"
            :title="isAllExpanded ? '全部折叠' : '全部展开'"
          >
            <template #icon>
              <DownOutlined v-if="!isAllExpanded" />
              <UpOutlined v-else />
            </template>
          </a-button>
        </template>
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'expand'">
            <a-button
              v-if="record.level === 2"
              type="text"
              size="small"
              @click="handleExpand(!expandedRowKeys.includes(record.id), record)"
              :title="expandedRowKeys.includes(record.id) ? '折叠' : '展开'"
            >
              <template #icon>
                <DownOutlined v-if="!expandedRowKeys.includes(record.id)" />
                <UpOutlined v-else />
              </template>
            </a-button>
          </template>
          <template v-else-if="column.key === 'step_number'">
            <span v-if="record.level === 1">{{ record.step_number || '' }}</span>
            <template v-else>
              <!-- Worker 序号：双击可编辑 -->
              <span 
                v-if="editingWorkerId !== record.id"
                style="cursor: pointer; color: #1890ff;"
                :title="'双击编辑序号'"
                @dblclick="startEditWorkerOrder(record)"
              >
                {{ getWorkerStepNumber(record) }}
              </span>
              <a-input-number
                v-else
                v-model:value="editingWorkerOrder"
                :min="0"
                size="small"
                style="width: 60px;"
                @blur="saveWorkerOrder(record)"
                @pressEnter="saveWorkerOrder(record)"
                @keyup.esc="cancelEditWorkerOrder"
              />
            </template>
          </template>
          <template v-else-if="column.key === 'step_name'">
            <span :style="{ paddingLeft: record.level === 2 ? '24px' : '0' }">
              <span v-if="record.level === 2">└─ </span>
              {{ record.step_name }}
              <span v-if="record.level === 2" style="color: #999; font-size: 12px; margin-left: 4px;">({{ record.id }})</span>
              <span v-if="record.level === 2" style="font-size: 12px; margin-left: 4px;">
                <span v-if="record.step_code" style="color: #1890ff;">({{ record.step_code }})</span>
                <span v-else style="color: #ff4d4f;">(NULL)</span>
              </span>
            </span>
          </template>
          <template v-else-if="column.key === 'level'">
            <a-tag :color="record.level === 1 ? 'blue' : 'green'">
              {{ record.level === 1 ? '步骤' : 'Worker' }}
            </a-tag>
          </template>
          <template v-else-if="column.key === 'match_rules'">
            <div v-if="record.level === 1" class="match-rules-cell">
              <div v-if="record.match_uid">
                <a-tag color="blue" size="small">UID: {{ record.match_uid }}</a-tag>
              </div>
              <div v-if="record.match_uid_suffix">
                <a-tag color="green" size="small">末尾: {{ record.match_uid_suffix }}</a-tag>
              </div>
              <div v-if="!record.match_uid && !record.match_uid_suffix" style="color: #999; font-size: 12px;">
                无
              </div>
            </div>
            <span v-else style="color: #999">-</span>
          </template>
          <template v-else-if="column.key === 'is_default'">
            <a-tag v-if="record.level === 1" :color="record.is_default === 1 ? 'red' : 'default'">
              {{ record.is_default === 1 ? '默认' : '-' }}
            </a-tag>
            <span v-else style="color: #999">-</span>
          </template>
          <template v-else-if="column.key === 'can_retry'">
            <a-tag v-if="record.level === 1" :color="record.can_retry === 1 ? 'green' : 'default'">
              {{ record.can_retry === 1 ? '可重试' : '不可重试' }}
            </a-tag>
            <span v-else style="color: #999">-</span>
          </template>
          <template v-else-if="column.key === 'is_enabled'">
            <a-switch 
              v-if="record.level === 2" 
              v-model:checked="record.is_enabled"
              :checked-value="1"
              :unchecked-value="0"
              @change="(checked) => handleToggleEnabled(record, checked)"
              :loading="record._updatingEnabled"
            />
            <span v-else style="color: #999">-</span>
          </template>
          <template v-else-if="column.key === 'is_skippable_on_failure'">
            <a-tag v-if="record.level === 2" :color="record.is_skippable_on_failure === 1 ? 'orange' : 'blue'">
              {{ record.is_skippable_on_failure === 1 ? '可跳过' : '必须成功' }}
            </a-tag>
            <span v-else style="color: #999">-</span>
          </template>
          <template v-else-if="column.key === 'action'">
            <!-- 大屏幕：显示部分按钮 + 下拉菜单 -->
            <a-space v-if="screenWidth >= breakpoints.desktop">
              <a-button type="link" size="small" @click="handleEdit(record)">编辑</a-button>
              <a-dropdown>
                <template #overlay>
                  <a-menu>
                    <a-menu-item v-if="record.level === 1" key="matchRules" @click="handleEditStepMatchRules(record)">
                      编辑匹配规则
                    </a-menu-item>
                    <a-menu-item v-if="record.level === 1 && record.is_default !== 1" key="setDefault" @click="handleSetDefaultStep(record)">
                      设为默认
                    </a-menu-item>
                    <a-menu-item v-if="record.level === 1" key="addWorker" @click="handleAddWorker(record)">
                      添加Worker
                    </a-menu-item>
                    <a-menu-divider />
                    <a-menu-item key="delete" danger>
                      <a-popconfirm title="确定删除吗？" @confirm="handleDelete(record.id)" placement="left">
                        <span>删除</span>
                      </a-popconfirm>
                    </a-menu-item>
                  </a-menu>
                </template>
                <a-button type="link" size="small">
                  更多 <DownOutlined />
                </a-button>
              </a-dropdown>
            </a-space>
            
            <!-- 中小屏幕：所有操作都在下拉菜单中 -->
            <a-dropdown v-else>
              <template #overlay>
                <a-menu>
                  <a-menu-item key="edit" @click="handleEdit(record)">
                    编辑
                  </a-menu-item>
                  <a-menu-item v-if="record.level === 1" key="matchRules" @click="handleEditStepMatchRules(record)">
                    编辑匹配规则
                  </a-menu-item>
                  <a-menu-item v-if="record.level === 1 && record.is_default !== 1" key="setDefault" @click="handleSetDefaultStep(record)">
                    设为默认
                  </a-menu-item>
                  <a-menu-item v-if="record.level === 1" key="addWorker" @click="handleAddWorker(record)">
                    添加Worker
                  </a-menu-item>
                  <a-menu-divider />
                  <a-menu-item key="delete" danger>
                    <a-popconfirm title="确定删除吗？" @confirm="handleDelete(record.id)" placement="left">
                      <span>删除</span>
                    </a-popconfirm>
                  </a-menu-item>
                </a-menu>
              </template>
              <a-button type="link" size="small">
                <MoreOutlined />
              </a-button>
            </a-dropdown>
          </template>
        </template>
      </a-table>
    </a-card>

    <!-- 步骤/Worker编辑弹窗 -->
    <a-modal
      v-model:open="modalVisible"
      :title="isEdit ? (formData.level === 1 ? '编辑步骤' : '编辑Worker') : (formData.level === 1 ? '新建步骤' : '新建Worker')"
      width="800px"
      @ok="handleSubmit"
      @cancel="handleCancel"
    >
      <a-form :model="formData" :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
        <a-form-item label="步骤层级" v-if="!isEdit">
          <a-radio-group v-model:value="formData.level" :disabled="isEdit">
            <a-radio :value="1">步骤</a-radio>
            <a-radio :value="2">Worker</a-radio>
          </a-radio-group>
        </a-form-item>
        <a-form-item label="父步骤" v-if="formData.level === 2" required>
          <a-select
            v-model:value="formData.parent_id"
            placeholder="请选择父步骤"
            style="width: 100%"
            :disabled="isEdit"
          >
            <a-select-option v-for="step in parentSteps" :key="step.id" :value="step.id">
              {{ step.step_name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="名称" required>
          <a-input v-model:value="formData.step_name" :placeholder="formData.level === 1 ? '请输入步骤名称' : '请输入Worker名称'" />
        </a-form-item>
        <a-form-item label="前端展示名称">
          <a-input v-model:value="formData.description" placeholder="请输入前端展示名称" />
        </a-form-item>
        <a-form-item label="代码">
          <a-input v-model:value="formData.step_code" placeholder="请输入代码" />
        </a-form-item>
        <a-form-item label="步骤">
          <a-input-number v-model:value="formData.step_order" :min="0" style="width: 100%" />
        </a-form-item>
        <a-form-item label="可重试" v-if="formData.level === 1">
          <a-checkbox v-model:checked="formData.can_retry">
            勾选后，该步骤可以被无限次重复请求
          </a-checkbox>
        </a-form-item>
        
        <!-- Worker 特有字段 -->
        <template v-if="formData.level === 2">
          <a-form-item label="出参（JSON）">
            <CodeEditor
              v-model="formData.output_params"
              language="json"
              :height="200"
            />
            <div style="margin-top: 4px; color: #999; font-size: 12px;">
              提示：输入有效的JSON格式数据，例如：{"result": "value"}
            </div>
          </a-form-item>
          <a-form-item label="任务类型" required>
            <a-radio-group v-model:value="formData.task_model_class">
              <a-radio value="light">轻任务</a-radio>
              <a-radio value="heavy">重任务</a-radio>
            </a-radio-group>
            <div style="margin-top: 4px; color: #999; font-size: 12px;">
              运行时会按任务类型自动匹配对应模型链路，Worker 不再默认绑定具体模型。
            </div>
          </a-form-item>
          <a-collapse ghost>
            <a-collapse-panel key="legacy-model" header="兼容配置（已废弃）">
              <a-form-item label="旧大模型ID">
                <a-select
                  v-model:value="formData.model_id"
                  placeholder="仅历史兼容或特殊覆盖场景使用"
                  style="width: 100%"
                  allowClear
                >
                  <a-select-option v-for="model in modelList" :key="model.id" :value="model.id">
                    {{ model.model_name }} ({{ model.model_id }})
                  </a-select-option>
                </a-select>
                <div style="margin-top: 4px; color: #999; font-size: 12px;">
                  该字段已废弃，仅用于兼容旧 Worker 配置；新配置请使用上方“任务类型”。
                </div>
              </a-form-item>
            </a-collapse-panel>
          </a-collapse>
          <a-form-item label="提示词结构">
            <a-select
              v-model:value="formData.prompt_template_id"
              placeholder="请选择提示词结构"
              style="width: 100%"
              allowClear
              show-search
              :filter-option="filterPromptTemplateOption"
            >
              <a-select-option v-for="template in promptTemplateList" :key="template.id" :value="template.id" :label="template.title">
                {{ template.title }}
              </a-select-option>
            </a-select>
            <div style="margin-top: 4px; color: #999; font-size: 12px;">
              提示：选择知识类型为"提示词结构"的知识作为模板
            </div>
          </a-form-item>
          <!-- 执行条件暂时隐藏 -->
          <!-- <a-form-item label="执行条件（JSON）">
            <CodeEditor
              v-model="formData.execution_condition"
              language="json"
              :height="300"
            />
            <div style="margin-top: 4px; color: #999; font-size: 12px;">
              <div>提示：定义Worker的执行条件，如果条件不满足则跳过执行</div>
              <div style="margin-top: 4px;">示例：</div>
              <pre style="background: #f5f5f5; padding: 8px; border-radius: 4px; font-size: 12px; margin-top: 4px;">{
  "type": "simple",
  "depends_on": [1],
  "rules": [
    {
      "worker_id": 1,
      "field": "result.status",
      "operator": "equals",
      "value": "need_refine"
    }
  ],
  "logic": "AND"
}</pre>
              <div style="margin-top: 4px;">留空表示无条件，总是执行</div>
            </div>
          </a-form-item> -->
          <a-form-item label="是否启用">
            <a-switch v-model:checked="formData.is_enabled" :checked-value="1" :unchecked-value="0" />
            <div style="margin-top: 4px; color: #999; font-size: 12px;">
              禁用时，Worker将直接跳过执行，不检查执行条件
            </div>
          </a-form-item>
          <a-form-item label="执行失败时是否可跳过">
            <a-switch v-model:checked="formData.is_skippable_on_failure" :checked-value="1" :unchecked-value="0" />
            <div style="margin-top: 4px; color: #999; font-size: 12px;">
              开启时，Worker执行失败将标记为跳过并继续执行下一个Worker；关闭时，Worker失败将停止整个执行计划
            </div>
          </a-form-item>
        </template>
        
        <a-form-item label="备注" v-if="formData.level === 1">
          <a-textarea v-model:value="formData.remark" :rows="3" placeholder="请输入备注" />
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 编辑步骤匹配规则弹窗 -->
    <a-modal
      v-model:open="editMatchRulesModalVisible"
      title="编辑步骤匹配规则"
      width="600px"
      @ok="handleUpdateMatchRules"
      @cancel="editMatchRulesModalVisible = false"
    >
      <a-form :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
        <a-form-item label="步骤名称">
          <a-input v-model:value="matchRulesForm.step_name" disabled />
        </a-form-item>
        <a-form-item label="步骤顺序">
          <a-input-number v-model:value="matchRulesForm.step_order" disabled style="width: 100%" />
        </a-form-item>
        <a-form-item label="匹配UID">
          <a-input v-model:value="matchRulesForm.match_uid" placeholder="逗号分隔，如：uid1,uid2,uid3" />
          <div style="margin-top: 4px; color: #999; font-size: 12px;">精确匹配UID，多个用逗号分隔</div>
        </a-form-item>
        <a-form-item label="匹配UID末尾">
          <a-input v-model:value="matchRulesForm.match_uid_suffix" placeholder="逗号分隔，如：0,1,2" />
          <div style="margin-top: 4px; color: #999; font-size: 12px;">匹配UID末尾数字，多个用逗号分隔</div>
        </a-form-item>
        <a-form-item label="优先级">
          <a-input-number v-model:value="matchRulesForm.priority" :min="0" style="width: 100%" />
          <div style="margin-top: 4px; color: #999; font-size: 12px;">数字越小优先级越高，0最高</div>
        </a-form-item>
        <a-form-item label="是否默认">
          <a-switch v-model:checked="matchRulesForm.is_default" :checked-value="1" :unchecked-value="0" />
          <div style="margin-top: 4px; color: #999; font-size: 12px;">每个步骤顺序的第一个步骤自动设置为默认</div>
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 关联知识弹窗 -->
    <a-modal
      v-model:open="associateModalVisible"
      title="关联知识"
      width="900px"
      @ok="handleAssociateSubmit"
      @cancel="associateModalVisible = false"
    >
      <a-form :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }" style="margin-bottom: 16px;">
        <a-form-item label="知识类型">
          <a-select
            v-model:value="associateForm.typeFilter"
            placeholder="全部类型"
            style="width: 200px; margin-right: 8px;"
            allowClear
            @blur="() => loadKnowledgeList(true)"
            @change="() => loadKnowledgeList(true)"
          >
            <a-select-option value="prompt">提示词</a-select-option>
            <a-select-option value="dataflow">数据流</a-select-option>
            <a-select-option value="pin_info">针脚信息</a-select-option>
            <a-select-option value="case">典型案例</a-select-option>
            <a-select-option value="principle">系统原理</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="标题搜索">
          <a-input-search
            v-model:value="associateForm.titleSearch"
            placeholder="按标题搜索"
            style="width: 300px;"
            @search="() => loadKnowledgeList(true)"
            @blur="() => loadKnowledgeList(true)"
          />
        </a-form-item>
      </a-form>

      <a-table
        :columns="knowledgeColumns"
        :data-source="knowledgeList"
        :loading="knowledgeLoading"
        :pagination="{ 
          pageSize: 5,
          current: associatePagination.current,
          total: associatePagination.total,
          showTotal: (total) => `共 ${total} 条`,
          showSizeChanger: false
        }"
        @change="handleKnowledgeTableChange"
        row-key="id"
        :row-selection="{ 
          selectedRowKeys: selectedKnowledgeList.map(k => k.id),
          onSelect: handleKnowledgeSelect,
          onSelectAll: handleKnowledgeSelectAll,
          getCheckboxProps: (record) => ({
            checked: isKnowledgeSelected(record.id)
          })
        }"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'type'">
            <a-tag :color="getKnowledgeTypeColor(record.type_code)">
              {{ record.type_name }}
            </a-tag>
          </template>
          <template v-else-if="column.key === 'content'">
            <div style="max-width: 300px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
              {{ record.content || '-' }}
            </div>
          </template>
        </template>
      </a-table>

      <div v-if="selectedKnowledgeList.length > 0" style="margin-top: 16px; padding: 12px; background: #f0f0f0; border-radius: 4px;">
        <div style="margin-bottom: 8px; font-weight: 500;">已选择的知识（可拖拽排序或输入序号）：</div>
        <draggable
          v-model="selectedKnowledgeList"
          item-key="id"
          handle=".drag-handle"
          @end="updateSortOrder"
        >
          <template #item="{ element, index }">
            <div style="padding: 8px; margin-bottom: 4px; background: white; border-radius: 4px; display: flex; align-items: center;">
              <span class="drag-handle" style="cursor: move; margin-right: 8px; color: #999; font-size: 16px;">☰</span>
              <span style="color: #999; margin-right: 8px; min-width: 60px;">
                序号：
                <a-input-number
                  v-model:value="element.sort_order"
                  :min="1"
                  :max="selectedKnowledgeList.length"
                  size="small"
                  style="width: 60px;"
                  @change="updateSortOrder"
                />
              </span>
              <a-tag :color="getKnowledgeTypeColor(element.type_code)" style="margin-right: 8px;">
                {{ element.type_name }}
              </a-tag>
              <span style="flex: 1;">{{ element.title }}</span>
              <a-button type="link" danger size="small" @click="removeSelectedKnowledge(element.id)">移除</a-button>
            </div>
          </template>
        </draggable>
      </div>
    </a-modal>

    <!-- 设置提示词结构弹窗 -->
    <a-modal
      v-model:open="setPromptTemplateModalVisible"
      title="关联提示词结构"
      width="600px"
      @ok="handleSetPromptTemplateSubmit"
      @cancel="setPromptTemplateModalVisible = false"
    >
      <a-form :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
        <a-form-item label="提示词结构" required>
          <a-select
            v-model:value="selectedPromptTemplateId"
            placeholder="请选择提示词结构"
            style="width: 100%"
            show-search
            :filter-option="filterPromptTemplateOption"
          >
            <a-select-option v-for="template in promptTemplateList" :key="template.id" :value="template.id" :label="template.title">
              {{ template.title }}
            </a-select-option>
          </a-select>
          <div style="margin-top: 4px; color: #999; font-size: 12px;">
            提示：选择知识类型为"提示词结构"的知识作为模板
          </div>
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 预览提示词结构弹窗 -->
    <a-modal
      v-model:open="previewModalVisible"
      title="预览提示词结构"
      width="1200px"
      :footer="null"
    >
      <a-row :gutter="16">
        <a-col :span="12">
          <a-card title="提示词结构（原始）" size="small">
            <CodeEditor
              :model-value="previewData.original_template || ''"
              language="xml"
              :height="500"
              :read-only="true"
            />
          </a-card>
        </a-col>
        <a-col :span="12">
          <a-card title="最终结构（组合后）" size="small">
            <CodeEditor
              :model-value="previewData.final_template || ''"
              language="xml"
              :height="500"
              :read-only="true"
            />
          </a-card>
        </a-col>
      </a-row>
      <a-card title="变量映射关系" size="small" style="margin-top: 16px;">
        <a-descriptions :column="1" bordered>
          <a-descriptions-item label="{prompt}">
            <div v-if="previewData.variable_mapping?.prompt && previewData.variable_mapping.prompt.length > 0">
              <div v-for="item in previewData.variable_mapping.prompt" :key="item.id" style="margin-bottom: 8px; padding: 8px; background: #f5f5f5; border-radius: 4px;">
                <div style="font-weight: 500; margin-bottom: 4px;">{{ item.title }}</div>
                <div style="color: #666; font-size: 12px;">{{ item.content_preview }}</div>
              </div>
            </div>
            <span v-else style="color: #999;">未关联，将替换为空</span>
          </a-descriptions-item>
          <a-descriptions-item label="{dataflow}">
            <div v-if="previewData.variable_mapping?.dataflow && previewData.variable_mapping.dataflow.length > 0">
              <div v-for="item in previewData.variable_mapping.dataflow" :key="item.id" style="margin-bottom: 8px; padding: 8px; background: #f5f5f5; border-radius: 4px;">
                <div style="font-weight: 500; margin-bottom: 4px;">{{ item.title }}</div>
                <div style="color: #666; font-size: 12px;">{{ item.content_preview }}</div>
              </div>
            </div>
            <span v-else style="color: #999;">未关联，将替换为空</span>
          </a-descriptions-item>
          <a-descriptions-item label="{pin_info}">
            <div v-if="previewData.variable_mapping?.pin_info && previewData.variable_mapping.pin_info.length > 0">
              <div v-for="item in previewData.variable_mapping.pin_info" :key="item.id" style="margin-bottom: 8px; padding: 8px; background: #f5f5f5; border-radius: 4px;">
                <div style="font-weight: 500; margin-bottom: 4px;">{{ item.title }}</div>
                <div style="color: #666; font-size: 12px;">{{ item.content_preview }}</div>
              </div>
            </div>
            <span v-else style="color: #999;">未关联，将替换为空</span>
          </a-descriptions-item>
          <a-descriptions-item label="{case}">
            <div v-if="previewData.variable_mapping?.case && previewData.variable_mapping.case.length > 0">
              <div v-for="item in previewData.variable_mapping.case" :key="item.id" style="margin-bottom: 8px; padding: 8px; background: #f5f5f5; border-radius: 4px;">
                <div style="font-weight: 500; margin-bottom: 4px;">{{ item.title }}</div>
                <div style="color: #666; font-size: 12px;">{{ item.content_preview }}</div>
              </div>
            </div>
            <span v-else style="color: #999;">未关联，将替换为空</span>
          </a-descriptions-item>
          <a-descriptions-item label="{principle}">
            <div v-if="previewData.variable_mapping?.principle && previewData.variable_mapping.principle.length > 0">
              <div v-for="item in previewData.variable_mapping.principle" :key="item.id" style="margin-bottom: 8px; padding: 8px; background: #f5f5f5; border-radius: 4px;">
                <div style="font-weight: 500; margin-bottom: 4px;">{{ item.title }}</div>
                <div style="color: #666; font-size: 12px;">{{ item.content_preview }}</div>
              </div>
            </div>
            <span v-else style="color: #999;">未关联，将替换为空</span>
          </a-descriptions-item>
        </a-descriptions>
      </a-card>
    </a-modal>

    <!-- 出参版本变更弹窗 -->
    <VersionChangeModal
      v-model:visible="versionChangeModalVisible"
      :current-version="schemeVersion"
      @confirm="handleVersionChangeConfirm"
      @skip="handleVersionChangeSkip"
      @cancel="handleVersionChangeCancel"
    />
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed, watch, onUnmounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { message } from 'ant-design-vue';
import { PlusOutlined, DownOutlined, UpOutlined, MoreOutlined, UnorderedListOutlined } from '@ant-design/icons-vue';
import draggable from 'vuedraggable';
import CodeEditor from '../components/CodeEditor.vue';
import VersionChangeModal from '../components/OutputParams/VersionChangeModal.vue';
import request from '../api/request';
import { createCallStep, updateCallStep, deleteCallStep } from '../api/callStep';
import { getCallSchemeById, getSchemeSteps, addSchemeStep, updateSchemeStep, setDefaultStep } from '../api/callScheme';
import { getModels } from '../api/model';
import { searchKnowledge } from '../api/knowledge';
import { getKnowledgeTypes } from '../api/knowledgeType';
import {
  associateKnowledges,
  setPromptTemplate,
  getAssociatedKnowledges,
  previewPromptTemplate
} from '../api/workerPrompt';

export default {
  name: 'CallStepManage',
  components: {
    PlusOutlined,
    DownOutlined,
    UpOutlined,
    MoreOutlined,
    UnorderedListOutlined,
    CodeEditor,
    VersionChangeModal,
    draggable
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const schemeId = computed(() => parseInt(route.params.schemeId));
    const schemeName = ref('');
    const loading = ref(false);
    const dataSource = ref([]);
    const modalVisible = ref(false);
    const isEdit = ref(false);
    const currentId = ref(null);
    const modelList = ref([]);
    const promptList = ref([]);
    const promptTemplateList = ref([]);
    const parentSteps = ref([]);
    const modelsLoaded = ref(false); // 标记模型列表是否已加载
    const promptsLoaded = ref(false); // 标记提示词列表是否已加载
    const promptTemplatesLoaded = ref(false); // 标记提示词结构列表是否已加载
    
    // 出参版本变更相关
    const versionChangeModalVisible = ref(false);
    const originalOutputParams = ref(''); // 编辑时保存原始出参
    const schemeVersion = ref('v1.0.0'); // 当前方案出参版本
    const pendingSubmitData = ref(null); // 待提交的数据（等待版本确认）
    
    // 响应式屏幕尺寸
    const screenWidth = ref(window.innerWidth);
    
    // 断点定义
    const breakpoints = {
      mobile: 768,    // 移动端
      tablet: 1024,   // 平板
      desktop: 1440   // 桌面
    };
    
    // Worker 知识关联相关
    const workerKnowledgeMap = ref({}); // { workerId: [knowledge1, knowledge2, ...] }
    const workerPromptTemplateMap = ref({}); // { workerId: { id, title, content } } 提示词结构
    const expandedWorkers = ref({}); // { workerId: ['knowledge'] } 控制展开状态
    const expandedRowKeys = ref([]); // 展开的行键数组（用于控制表格行的展开）
    const associateModalVisible = ref(false);
    const setPromptTemplateModalVisible = ref(false);
    const selectedPromptTemplateId = ref(null);
    const currentWorkerId = ref(null);
    const knowledgeList = ref([]);
    const knowledgeLoading = ref(false);
    const selectedKnowledgeList = ref([]); // 已选择的知识列表（用于排序）
    const editMatchRulesModalVisible = ref(false);
    const currentEditStepId = ref(null); // 当前编辑的步骤ID（ai_call_scheme_step表的id）
    const associatePagination = reactive({
      current: 1,
      total: 0,
      pageSize: 5
    });
    
    // 预览相关
    const previewModalVisible = ref(false);
    const previewData = ref({
      original_template: '',
      final_template: '',
      variable_mapping: {}
    });
    
    const associateForm = reactive({
      typeFilter: null,
      titleSearch: ''
    });

    const matchRulesForm = reactive({
      scheme_step_id: null,
      step_name: '',
      step_order: 0,
      match_uid: '',
      match_uid_suffix: '',
      priority: 0,
      is_default: 0
    });

    // Worker 序号编辑相关
    const editingWorkerId = ref(null);
    const editingWorkerOrder = ref(0);

    const formData = reactive({
      level: 1,
      parent_id: null,
      step_name: '',
      step_code: '',
      step_order: 0,
      description: '',
      remark: '',
      input_params: '',
      output_params: '',
      task_model_class: 'light',
      model_id: null,
      prompt_template_id: null,
      execution_condition: '',
      can_retry: false,
      is_enabled: true,
      is_skippable_on_failure: false
    });
    
    const knowledgeColumns = [
      { title: '标题', dataIndex: 'title', key: 'title', width: 200 },
      { title: '类型', key: 'type', width: 120 },
      { title: '内容预览', key: 'content', width: 300 },
      { title: '创建时间', dataIndex: 'create_time', key: 'create_time', width: 180 }
    ];

    // 动态列配置 - 根据屏幕宽度显示不同的列
    const columns = computed(() => {
      const width = screenWidth.value;
      
      // 基础列（始终显示）
      const baseColumns = [
        { title: '', key: 'expand', width: 50 },
        { title: '名称', key: 'step_name', minWidth: 200, ellipsis: true }
      ];
      
      // 移动端（<768px）：只显示名称和操作
      if (width < breakpoints.mobile) {
        return [
          ...baseColumns,
          { title: '操作', key: 'action', width: 80, fixed: 'right' }
        ];
      }
      
      // 平板（768-1024px）：增加类型、是否启用
      if (width < breakpoints.tablet) {
        return [
          ...baseColumns,
          { title: '类型', key: 'level', width: 100 },
          { title: '启用', key: 'is_enabled', width: 80 },
          { title: '操作', key: 'action', width: 80, fixed: 'right' }
        ];
      }
      
      // 桌面（1024-1440px）：增加步骤序号、匹配规则
      if (width < breakpoints.desktop) {
        return [
          { title: '', key: 'expand', width: 50 },
          { title: '步骤序号', key: 'step_number', width: 100 },
          { title: '名称', key: 'step_name', minWidth: 180, ellipsis: true },
          { title: '类型', key: 'level', width: 90 },
          { title: '匹配规则', key: 'match_rules', width: 150, ellipsis: true },
          { title: '启用', key: 'is_enabled', width: 80 },
          { title: '操作', key: 'action', width: 100, fixed: 'right' }
        ];
      }
      
      // 大屏（≥1440px）：显示所有列
      return [
        { title: '', key: 'expand', width: 50 },
        { title: '步骤序号', key: 'step_number', width: 100 },
        { title: '名称', key: 'step_name', minWidth: 200, ellipsis: true },
        { title: '类型', key: 'level', width: 90 },
        { title: '匹配规则', key: 'match_rules', width: 180, ellipsis: true },
        { title: '默认', key: 'is_default', width: 80 },
        { title: '可重试', key: 'can_retry', width: 90 },
        { title: '启用', key: 'is_enabled', width: 80 },
        { title: '是否可跳过', key: 'is_skippable_on_failure', width: 100 },
        { title: '操作', key: 'action', width: 120, fixed: 'right' }
      ];
    });

    // 加载流程信息
    const loadScheme = async () => {
      try {
        const res = await getCallSchemeById(schemeId.value);
        if (res.code === 200) {
          schemeName.value = res.data.scheme_name || 'AI调用流程';
        }
      } catch (error) {
        console.error('加载流程信息失败:', error);
      }
    };

    // 加载步骤列表
    const loadSteps = async () => {
      loading.value = true;
      // 清空展开状态，确保默认全部折叠
      expandedRowKeys.value = [];
      expandedWorkers.value = {};
      try {
        // 获取流程关联的步骤和worker（后端已经返回了完整的数据结构）
        console.log('加载步骤列表 - schemeId:', schemeId.value, '类型:', typeof schemeId.value);
        const schemeStepsRes = await getSchemeSteps(schemeId.value);
        console.log('步骤列表响应 - schemeId:', schemeId.value, '步骤数量:', schemeStepsRes.data?.steps?.length || 0);
        
        if (schemeStepsRes.code === 200 && schemeStepsRes.data) {
          // 后端返回的数据结构是 { scheme: {...}, steps: [{ ...step, sub_steps: [worker1, worker2, ...] }] }
          const stepsData = schemeStepsRes.data.steps || [];
          
          if (!Array.isArray(stepsData)) {
            dataSource.value = [];
            parentSteps.value = [];
            return;
          }
          
          // 按步骤的 step_order、priority、id 排序
          const sortedSteps = [...stepsData].sort((a, b) => {
            // 先按 step_order 排序（从 scheme_step 表中获取）
            const orderA = a.step_order || 0;
            const orderB = b.step_order || 0;
            if (orderA !== orderB) {
              return orderA - orderB;
            }
            // 如果 step_order 相同，按 priority 排序（数字越小优先级越高，0最高）
            const priorityA = a.priority !== undefined ? a.priority : 0;
            const priorityB = b.priority !== undefined ? b.priority : 0;
            if (priorityA !== priorityB) {
              return priorityA - priorityB;
            }
            // 如果 priority 也相同，按 id 排序（创建时间早的优先）
            const idA = a.call_step_id || a.id || 0;
            const idB = b.call_step_id || b.id || 0;
            return idA - idB;
          });
          
          // 构建层级结构：每个步骤后面紧跟着它的worker
          const result = [];
          
          for (const schemeStep of sortedSteps) {
            // 获取步骤的完整信息（从 call_step 表和 ai_call_scheme_step 表）
            const step = {
              id: schemeStep.call_step_id || schemeStep.id, // call_step 表的 id
              scheme_step_id: schemeStep.id, // ai_call_scheme_step 表的 id（用于更新匹配规则）
              level: 1,
              step_name: schemeStep.step_name,
              step_code: schemeStep.step_code,
              step_order: schemeStep.step_order || 0,
              description: schemeStep.description,
              remark: schemeStep.remark || null,
              match_uid: schemeStep.match_uid || null,
              match_uid_suffix: schemeStep.match_uid_suffix || null,
              priority: schemeStep.priority !== undefined ? schemeStep.priority : 0,
              is_default: schemeStep.is_default !== undefined ? schemeStep.is_default : 0,
              can_retry: schemeStep.can_retry !== undefined ? schemeStep.can_retry : 0,
              parent_id: null,
              input_params: null,
              output_params: null,
              model_id: null,
              prompt_template_id: null,
              prompt_knowledge_ids: null
            };
            
            // 为步骤添加序号（使用 step_order）
            const stepWithNumber = { ...step, step_number: `步骤${step.step_order}` };
            result.push(stepWithNumber);
            
            // 获取该步骤的所有worker（从 sub_steps 字段）
            const subSteps = schemeStep.sub_steps || [];
            const stepWorkers = subSteps
              .map(worker => {
                // 确保 is_enabled 是数字 1 或 0
                let isEnabled = worker.is_enabled;
                if (isEnabled === undefined || isEnabled === null) {
                  isEnabled = 1; // 默认启用
                } else {
                  isEnabled = isEnabled === 1 || isEnabled === true ? 1 : 0;
                }
                // 确保 is_skippable_on_failure 是数字 1 或 0
                let isSkippableOnFailure = worker.is_skippable_on_failure;
                if (isSkippableOnFailure === undefined || isSkippableOnFailure === null) {
                  isSkippableOnFailure = 0; // 默认必须成功
                } else {
                  isSkippableOnFailure = isSkippableOnFailure === 1 || isSkippableOnFailure === true ? 1 : 0;
                }
                return {
                  ...worker,
                  level: 2,
                  parent_id: step.id,
                  is_enabled: isEnabled,
                  is_skippable_on_failure: isSkippableOnFailure
                };
              })
              .sort((a, b) => {
                // Worker 按 step_order 和 id 排序
                if (a.step_order !== b.step_order) {
                  return (a.step_order || 0) - (b.step_order || 0);
                }
                return (a.id || 0) - (b.id || 0);
              });
            
            // 添加该步骤的worker（不添加序号）
            result.push(...stepWorkers);
            
            // 加载每个 Worker 的关联知识，并默认展开（并行请求优化）
            await Promise.all(stepWorkers.map(async (worker) => {
              await loadWorkerKnowledge(worker.id);
              // 默认展开 Worker 的知识列表
              if (!expandedWorkers.value[worker.id]) {
                expandedWorkers.value[worker.id] = ['knowledge'];
              }
            }));
            
          }
          
          dataSource.value = result;
          // 更新父步骤列表（只显示level=1的步骤，且属于当前流程）
          parentSteps.value = sortedSteps.map(ss => ({
            id: ss.call_step_id || ss.id,
            step_name: ss.step_name,
            step_code: ss.step_code,
            level: 1
          }));
        } else {
          dataSource.value = [];
          parentSteps.value = [];
        }
      } catch (error) {
        console.error('加载步骤列表失败:', error);
        message.error('加载步骤列表失败: ' + (error.response?.data?.message || error.message));
      } finally {
        loading.value = false;
      }
    };

    // 加载模型列表（按需加载）
    const loadModels = async () => {
      if (modelsLoaded.value) {
        return; // 已加载过，直接返回
      }
      try {
        const res = await getModels({ page: 1, pageSize: 500 });
        if (res.code === 200) {
          // res.data 是一个对象，包含 list 属性（数组）
          const models = res.data?.list || res.data || [];
          modelList.value = Array.isArray(models) ? models.filter(m => m.is_enabled === 1) : [];
          modelsLoaded.value = true;
        }
      } catch (error) {
        console.error('加载模型列表失败:', error);
      }
    };

    // 加载提示词列表（按需加载）
    const loadPrompts = async () => {
      if (promptsLoaded.value) {
        return; // 已加载过，直接返回
      }
      try {
        // 先获取知识类型，找到prompt类型的ID
        const typesRes = await getKnowledgeTypes();
        let promptTypeId = null;
        if (typesRes.code === 200 && typesRes.data) {
          const promptType = typesRes.data.find(t => t.type_code === 'prompt');
          if (promptType) {
            promptTypeId = promptType.id;
          }
        }
        
        const params = {
          types: promptTypeId ? [promptTypeId] : [],
          callStepIds: [],
          conditions: {},
          must: [],
          sort: 'update_time',
          order: 'desc',
          page: 1,
          pageSize: 1000
        };
        const res = await searchKnowledge(params);
        if (res.code === 200) {
          promptList.value = res.data.items || [];
          promptsLoaded.value = true;
        }
      } catch (error) {
        console.error('加载提示词列表失败:', error);
      }
    };

    // 加载提示词结构列表（按需加载）
    const loadPromptTemplates = async () => {
      if (promptTemplatesLoaded.value) {
        return;
      }
      try {
        const typesRes = await getKnowledgeTypes();
        let templateTypeId = null;
        if (typesRes.code === 200 && typesRes.data) {
          const templateType = typesRes.data.find(t => t.type_code === 'prompt_template');
          if (templateType) {
            templateTypeId = templateType.id;
          }
        }
        
        const params = {
          types: templateTypeId ? [templateTypeId] : [],
          callStepIds: [],
          conditions: {},
          must: [],
          sort: 'update_time',
          order: 'desc',
          page: 1,
          pageSize: 1000
        };
        const res = await searchKnowledge(params);
        if (res.code === 200) {
          promptTemplateList.value = res.data.items || [];
          promptTemplatesLoaded.value = true;
        }
      } catch (error) {
        console.error('加载提示词结构列表失败:', error);
      }
    };

    // 加载 Worker 的关联知识
    const loadWorkerKnowledge = async (workerId) => {
      try {
        const res = await getAssociatedKnowledges(workerId);
        if (res.code === 200 && res.data) {
          // 保存提示词结构
          if (res.data.prompt_template) {
            workerPromptTemplateMap.value[workerId] = {
              id: res.data.prompt_template.id,
              title: res.data.prompt_template.title,
              content: res.data.prompt_template.content
            };
          } else {
            workerPromptTemplateMap.value[workerId] = null;
          }
          
          // 保存关联的知识
          const knowledges = [];
          Object.keys(res.data.knowledges || {}).forEach(type => {
            const typeKnowledges = res.data.knowledges[type] || [];
            typeKnowledges.forEach(k => {
              knowledges.push({
                id: k.id,
                title: k.title,
                type: type,
                type_code: k.type_code,
                type_name: k.type_name,
                sort_order: k.sort_order || 0
              });
            });
          });
          // 按 sort_order 排序
          knowledges.sort((a, b) => a.sort_order - b.sort_order);
          workerKnowledgeMap.value[workerId] = knowledges;
        }
      } catch (error) {
        console.error(`加载 Worker ${workerId} 的关联知识失败:`, error);
      }
    };

    // 加载知识列表（用于关联知识弹窗）
    const loadKnowledgeList = async (resetPage = false) => {
      if (resetPage) {
        associatePagination.current = 1;
      }
      
      knowledgeLoading.value = true;
      try {
        const typesRes = await getKnowledgeTypes();
        const typeMap = {};
        if (typesRes.code === 200 && typesRes.data) {
          typesRes.data.forEach(t => {
            typeMap[t.type_code] = t.id;
          });
        }

        // 构建类型ID列表
        const typeIds = [];
        if (associateForm.typeFilter) {
          const typeId = typeMap[associateForm.typeFilter];
          if (typeId) {
            typeIds.push(typeId);
          }
        } else {
          // 如果没有筛选，加载所有相关类型
          ['prompt', 'dataflow', 'pin_info', 'case', 'principle'].forEach(code => {
            const typeId = typeMap[code];
            if (typeId) {
              typeIds.push(typeId);
            }
          });
        }

        const params = {
          types: typeIds,
          callStepIds: [],
          conditions: {},
          must: [],
          title: associateForm.titleSearch || undefined,
          sort: 'update_time',
          order: 'desc',
          page: associatePagination.current,
          pageSize: associatePagination.pageSize
        };
        const res = await searchKnowledge(params);
        if (res.code === 200) {
          knowledgeList.value = res.data.items || [];
          associatePagination.total = res.data.total || 0;
        }
      } catch (error) {
        console.error('加载知识列表失败:', error);
        message.error('加载知识列表失败');
      } finally {
        knowledgeLoading.value = false;
      }
    };

    // 处理知识列表表格变化（翻页）
    const handleKnowledgeTableChange = (pagination) => {
      associatePagination.current = pagination.current;
      loadKnowledgeList(false);
    };

    // 知识类型颜色映射
    const getKnowledgeTypeColor = (typeCode) => {
      const colorMap = {
        'prompt': 'blue',
        'dataflow': 'green',
        'pin_info': 'orange',
        'case': 'purple',
        'principle': 'cyan',
        'prompt_template': 'red'
      };
      return colorMap[typeCode] || 'default';
    };

    // 知识类型名称映射
    const getKnowledgeTypeName = (typeCode) => {
      const nameMap = {
        'prompt': '提示词',
        'dataflow': '数据流',
        'pin_info': '针脚信息',
        'case': '典型案例',
        'principle': '系统原理',
        'prompt_template': '提示词结构'
      };
      return nameMap[typeCode] || typeCode;
    };

    // 检查知识是否已选择
    const isKnowledgeSelected = (knowledgeId) => {
      return selectedKnowledgeList.value.some(k => k.id === knowledgeId);
    };

    // 选择知识
    const handleKnowledgeSelect = (record, selected) => {
      if (selected) {
        if (!isKnowledgeSelected(record.id)) {
          selectedKnowledgeList.value.push({
            id: record.id,
            title: record.title,
            type_code: record.type_code,
            type_name: record.type_name,
            sort_order: selectedKnowledgeList.value.length + 1
          });
          updateSortOrder();
        }
      } else {
        removeSelectedKnowledge(record.id);
      }
    };

    // 全选/取消全选
    const handleKnowledgeSelectAll = (selected, selectedRows, changeRows) => {
      if (selected) {
        changeRows.forEach(row => {
          if (!isKnowledgeSelected(row.id)) {
            selectedKnowledgeList.value.push({
              id: row.id,
              title: row.title,
              type_code: row.type_code,
              type_name: row.type_name,
              sort_order: selectedKnowledgeList.value.length + 1
            });
          }
        });
        updateSortOrder();
      } else {
        changeRows.forEach(row => {
          removeSelectedKnowledge(row.id);
        });
      }
    };

    // 移除已选择的知识
    const removeSelectedKnowledge = (knowledgeId) => {
      const index = selectedKnowledgeList.value.findIndex(k => k.id === knowledgeId);
      if (index > -1) {
        selectedKnowledgeList.value.splice(index, 1);
        // 更新排序
        updateSortOrder();
      }
    };

    // 更新排序
    const updateSortOrder = () => {
      // 按 sort_order 排序
      selectedKnowledgeList.value.sort((a, b) => a.sort_order - b.sort_order);
      // 重新分配序号，确保连续
      selectedKnowledgeList.value.forEach((k, i) => {
        k.sort_order = i + 1;
      });
    };

    // 关联知识
    const handleAssociateKnowledge = async (record) => {
      currentWorkerId.value = record.id;
      selectedKnowledgeList.value = [];
      associateForm.typeFilter = null;
      associateForm.titleSearch = '';
      
      // 加载当前 Worker 已关联的知识
      try {
        const res = await getAssociatedKnowledges(record.id);
        if (res.code === 200 && res.data) {
          const knowledges = [];
          Object.keys(res.data.knowledges || {}).forEach(type => {
            const typeKnowledges = res.data.knowledges[type] || [];
            typeKnowledges.forEach(k => {
              knowledges.push({
                id: k.id,
                title: k.title,
                type_code: k.type_code,
                type_name: k.type_name,
                sort_order: k.sort_order || 0
              });
            });
          });
          // 按 sort_order 排序
          knowledges.sort((a, b) => a.sort_order - b.sort_order);
          selectedKnowledgeList.value = knowledges;
          updateSortOrder();
        }
      } catch (error) {
        console.error('加载已关联知识失败:', error);
      }
      
      // 重置分页
      associatePagination.current = 1;
      await loadKnowledgeList(false);
      associateModalVisible.value = true;
    };

    // 提交关联知识
    const handleAssociateSubmit = async () => {
      if (!currentWorkerId.value) {
        return;
      }

      try {
        const knowledgeAssociations = selectedKnowledgeList.value.map((k, index) => ({
          knowledge_id: k.id,
          type: k.type_code,
          sort_order: index + 1
        }));

        const res = await associateKnowledges(currentWorkerId.value, knowledgeAssociations);
        if (res.code === 200) {
          message.success('关联知识成功');
          associateModalVisible.value = false;
          // 重新加载 Worker 的关联知识
          await loadWorkerKnowledge(currentWorkerId.value);
          // 重新加载步骤列表
          await loadSteps();
        } else {
          message.error(res.message || '关联知识失败');
        }
      } catch (error) {
        console.error('关联知识失败:', error);
        message.error('关联知识失败');
      }
    };

    // 预览提示词结构
    const handlePreviewTemplate = async (record) => {
      try {
        const res = await previewPromptTemplate(record.id);
        if (res.code === 200) {
          previewData.value = res.data || {};
          previewModalVisible.value = true;
        } else {
          message.error(res.message || '预览失败');
        }
      } catch (error) {
        console.error('预览提示词结构失败:', error);
        message.error('预览提示词结构失败');
      }
    };

    // 查看知识详情
    const handleEditKnowledge = (knowledgeId) => {
      router.push({ name: 'knowledge-edit', params: { id: knowledgeId } });
    };

    // 设置提示词结构（从列表操作）
    const handleSetPromptTemplate = async (record) => {
      currentWorkerId.value = record.id;
      currentId.value = null; // 清空编辑ID
      // 加载当前 Worker 的提示词结构
      try {
        const res = await getAssociatedKnowledges(record.id);
        if (res.code === 200 && res.data && res.data.prompt_template) {
          selectedPromptTemplateId.value = res.data.prompt_template.id;
        } else {
          selectedPromptTemplateId.value = null;
        }
      } catch (error) {
        console.error('加载提示词结构失败:', error);
        selectedPromptTemplateId.value = null;
      }
      // 打开提示词结构选择弹窗
      await loadPromptTemplates();
      setPromptTemplateModalVisible.value = true;
    };

    // 设置提示词结构（从编辑弹窗）
    const handleSetPromptTemplateFromModal = async () => {
      currentWorkerId.value = null; // 清空Worker ID
      // 加载当前编辑的 Worker 的提示词结构
      if (currentId.value && formData.prompt_template_id) {
        selectedPromptTemplateId.value = formData.prompt_template_id;
      } else {
        selectedPromptTemplateId.value = null;
      }
      await loadPromptTemplates();
      setPromptTemplateModalVisible.value = true;
    };

    // 提交设置提示词结构
    const handleSetPromptTemplateSubmit = async () => {
      if (!selectedPromptTemplateId.value) {
        message.warning('请选择提示词结构');
        return;
      }
      
      const targetWorkerId = currentWorkerId.value || currentId.value;
      if (!targetWorkerId) {
        message.error('Worker ID 不存在');
        return;
      }

      try {
        const res = await setPromptTemplate(targetWorkerId, selectedPromptTemplateId.value);
        if (res.code === 200) {
          message.success('设置提示词结构成功');
          setPromptTemplateModalVisible.value = false;
          selectedPromptTemplateId.value = null;
          
          // 重新加载 Worker 的关联知识
          await loadWorkerKnowledge(targetWorkerId);
          // 重新加载步骤列表
          await loadSteps();
          
          // 如果是从编辑弹窗打开的，更新表单数据
          if (currentId.value === targetWorkerId) {
            formData.prompt_template_id = selectedPromptTemplateId.value;
          }
        } else {
          message.error(res.message || '设置提示词结构失败');
        }
      } catch (error) {
        console.error('设置提示词结构失败:', error);
        message.error('设置提示词结构失败');
      }
    };

    // 处理表格行展开/收起
    const handleExpand = (expanded, record) => {
      if (expanded) {
        if (!expandedRowKeys.value.includes(record.id)) {
          expandedRowKeys.value.push(record.id);
        }
        // 如果是 Worker，默认展开知识列表
        if (record.level === 2 && !expandedWorkers.value[record.id]) {
          expandedWorkers.value[record.id] = ['knowledge'];
        }
      } else {
        const index = expandedRowKeys.value.indexOf(record.id);
        if (index > -1) {
          expandedRowKeys.value.splice(index, 1);
        }
      }
    };

    // 判断是否全部展开（只考虑Worker行）
    const isAllExpanded = computed(() => {
      const workerRows = dataSource.value.filter(item => item.level === 2);
      if (workerRows.length === 0) return false;
      return workerRows.every(item => expandedRowKeys.value.includes(item.id));
    });

    // 切换全部展开/折叠（只操作Worker行）
    const handleToggleExpandAll = () => {
      if (isAllExpanded.value) {
        // 全部折叠：移除所有Worker行的展开状态
        const workerIds = dataSource.value.filter(item => item.level === 2).map(item => item.id);
        expandedRowKeys.value = expandedRowKeys.value.filter(id => !workerIds.includes(id));
      } else {
        // 全部展开：添加所有Worker行的展开状态
        const workerRows = dataSource.value.filter(item => item.level === 2);
        const workerIds = workerRows.map(item => item.id);
        // 添加所有Worker的ID到展开列表（避免重复）
        workerIds.forEach(id => {
          if (!expandedRowKeys.value.includes(id)) {
            expandedRowKeys.value.push(id);
          }
        });
        // 为所有Worker默认展开知识列表
        workerRows.forEach(item => {
          if (!expandedWorkers.value[item.id]) {
            expandedWorkers.value[item.id] = ['knowledge'];
          }
        });
      }
    };

    // 提示词结构搜索过滤
    const filterPromptTemplateOption = (input, option) => {
      // 优先使用 label 属性，添加空值保护避免 VNode 结构差异导致的错误
      const label = option.label || option.children?.[0]?.children || '';
      return String(label).toLowerCase().indexOf(input.toLowerCase()) >= 0;
    };

    // 返回
    const handleBack = () => {
      router.push({ name: 'call-scheme' });
    };

    // 查看出参列表（新开标签页）
    const handleViewOutputParams = () => {
      const url = router.resolve({
        name: 'output-params-list',
        params: { schemeId: schemeId.value }
      });
      window.open(url.href, '_blank');
    };

    // 新建步骤
    const handleCreateStep = async () => {
      resetForm();
      formData.level = 1;
      isEdit.value = false;
      modalVisible.value = true;
    };

    // 新建Worker
    const handleCreateWorker = async () => {
      resetForm();
      formData.level = 2;
      isEdit.value = false;
      // Worker需要模型和提示词结构列表，按需加载
      await Promise.all([loadModels(), loadPromptTemplates()]);
      modalVisible.value = true;
    };

    // 添加Worker（在步骤下）
    const handleAddWorker = async (record) => {
      resetForm();
      formData.level = 2;
      formData.parent_id = record.id;
      isEdit.value = false;
      // Worker需要模型和提示词结构列表，按需加载
      await Promise.all([loadModels(), loadPromptTemplates()]);
      modalVisible.value = true;
    };

    // 编辑
    const handleEdit = async (record) => {
      resetForm();
      Object.assign(formData, {
        level: record.level,
        parent_id: record.parent_id,
        step_name: record.step_name,
        step_code: record.step_code || '',
        step_order: record.step_order || 0,
        description: record.description || '',
        remark: record.remark || '',
        input_params: record.input_params || '',
        output_params: record.output_params || '',
        task_model_class: record.task_model_class || 'light',
        model_id: record.model_id || null,
        prompt_template_id: record.prompt_template_id || null,
        execution_condition: record.execution_condition 
          ? (typeof record.execution_condition === 'string' 
              ? record.execution_condition 
              : JSON.stringify(record.execution_condition, null, 2))
          : '',
        can_retry: record.can_retry === 1 || record.can_retry === true,
        is_enabled: record.is_enabled !== undefined ? (record.is_enabled === 1 || record.is_enabled === true ? 1 : 0) : 1,
        is_skippable_on_failure: record.is_skippable_on_failure !== undefined ? (record.is_skippable_on_failure === 1 || record.is_skippable_on_failure === true ? 1 : 0) : 0
      });
      currentId.value = record.id;
      // 如果是步骤（level=1），保存 scheme_step_id 用于更新流程步骤关联
      if (record.level === 1) {
        currentEditStepId.value = record.scheme_step_id || null;
      } else {
        currentEditStepId.value = null;
        // Worker编辑时保存原始出参，用于检测变化
        originalOutputParams.value = record.output_params || '';
      }
      isEdit.value = true;
      // 如果是Worker，需要模型和提示词结构列表，按需加载
      if (record.level === 2) {
        await Promise.all([loadModels(), loadPromptTemplates()]);
      }
      modalVisible.value = true;
    };

    // 删除
    const handleDelete = async (id) => {
      try {
        await deleteCallStep(id);
        message.success('删除成功');
        loadSteps();
      } catch (error) {
        message.error('删除失败');
      }
    };

    // 提交
    const handleSubmit = async () => {
      // 验证JSON格式
      if (formData.level === 2) {
        if (formData.output_params) {
          try {
            JSON.parse(formData.output_params);
          } catch (e) {
            message.error('出参格式错误，请输入有效的JSON格式');
            return;
          }
        }
        if (formData.execution_condition && formData.execution_condition.trim()) {
          try {
            JSON.parse(formData.execution_condition);
          } catch (e) {
            message.error('执行条件格式错误，请输入有效的JSON格式');
            return;
          }
        }
      }

      try {
        const data = {
          level: formData.level,
          parent_id: formData.level === 2 ? formData.parent_id : null,
          step_name: formData.step_name,
          step_code: formData.step_code,
          step_order: formData.step_order,
          description: formData.description,
          remark: formData.remark
        };

        // 如果是步骤（level=1），添加 can_retry 字段
        if (formData.level === 1) {
          data.can_retry = formData.can_retry ? 1 : 0;
        }

        if (formData.level === 2) {
          data.output_params = formData.output_params || null;
          data.task_model_class = formData.task_model_class || 'light';
          data.model_id = formData.model_id || null;
          data.prompt_template_id = formData.prompt_template_id || null;
          data.is_enabled = formData.is_enabled === 1 || formData.is_enabled === true ? 1 : 0;
          data.is_skippable_on_failure = formData.is_skippable_on_failure === 1 || formData.is_skippable_on_failure === true ? 1 : 0;
          // 处理执行条件：如果为空字符串则设为null，否则解析JSON
          if (formData.execution_condition && formData.execution_condition.trim()) {
            try {
              data.execution_condition = JSON.stringify(JSON.parse(formData.execution_condition));
            } catch (e) {
              message.error('执行条件格式错误');
              return;
            }
          } else {
            data.execution_condition = null;
          }
        }

        if (isEdit.value) {
          // 如果是步骤（level=1），需要同时更新两个表
          if (formData.level === 1) {
            // 1. 更新 ai_call_step 表（基本信息）
            const stepData = {
              step_name: data.step_name,
              step_code: data.step_code,
              description: data.description,
              remark: data.remark,
              can_retry: formData.can_retry ? 1 : 0
            };
            await updateCallStep(currentId.value, stepData);
            
            // 2. 更新 ai_call_scheme_step 表（流程关联信息，包括 step_order）
            if (currentEditStepId.value) {
              await updateSchemeStep(currentEditStepId.value, {
                step_order: data.step_order,
                is_required: 1, // 保持原有值
                remark: data.remark || ''
              });
            }
          } else {
            // Worker 更新
            // 检测出参是否变化
            const outputParamsChanged = (formData.output_params || '') !== (originalOutputParams.value || '');
            
            if (outputParamsChanged && formData.output_params) {
              // 出参发生变化，显示版本变更弹窗
              pendingSubmitData.value = { data, workerId: currentId.value };
              // 获取当前方案版本
              try {
                const versionRes = await request.get(`/call-scheme/${schemeId.value}/output-params`);
                if (versionRes.data.code === 200) {
                  schemeVersion.value = versionRes.data.data.current_version || 'v1.0.0';
                }
              } catch (e) {
                schemeVersion.value = 'v1.0.0';
              }
              versionChangeModalVisible.value = true;
              return; // 等待用户确认
            }
            
            // 没有出参变化，直接更新
            await updateCallStep(currentId.value, data);
          }
          message.success('更新成功');
        } else {
          // 创建步骤
          const createRes = await createCallStep(data);
          
          // 如果是新建步骤（level=1），需要将其添加到当前方案中
          if (formData.level === 1 && createRes.code === 200 && createRes.data) {
            const newStepId = createRes.data.id;
            // 获取当前方案中步骤的最大顺序，新步骤的顺序为最大顺序+1
            // 从 dataSource 中获取所有步骤（level=1）的最大顺序
            const currentSteps = dataSource.value.filter(s => s.level === 1) || [];
            const maxOrder = currentSteps.length > 0 
              ? Math.max(...currentSteps.map(s => s.step_order || 0))
              : 0;
            
            // 将步骤添加到当前方案
            await addSchemeStep(schemeId.value, {
              call_step_id: newStepId,
              step_order: formData.step_order || (maxOrder + 1),
              is_required: 1
            });
          }
          
          message.success('创建成功');
        }
        modalVisible.value = false;
        loadSteps();
      } catch (error) {
        message.error(isEdit.value ? '更新失败' : '创建失败');
      }
    };

    // 取消
    const handleCancel = () => {
      modalVisible.value = false;
      resetForm();
    };

    // 版本变更确认
    const handleVersionChangeConfirm = async (versionData) => {
      if (!pendingSubmitData.value) return;
      
      try {
        const { data, workerId } = pendingSubmitData.value;
        
        // 先更新 Worker 数据
        await updateCallStep(workerId, data);
        
        // 然后记录版本变更
        await request.post(`/call-step/${workerId}/output-params`, {
          output_params: data.output_params,
          version_type: versionData.versionType,
          change_description: versionData.changeDescription
        });
        
        message.success(`更新成功，版本已升级至 ${versionData.newVersion}`);
        modalVisible.value = false;
        loadSteps();
      } catch (error) {
        console.error('更新失败:', error);
        message.error('更新失败');
      } finally {
        pendingSubmitData.value = null;
      }
    };

    // 跳过版本记录
    const handleVersionChangeSkip = async () => {
      if (!pendingSubmitData.value) return;
      
      try {
        const { data, workerId } = pendingSubmitData.value;
        await updateCallStep(workerId, data);
        message.success('更新成功（未记录版本变更）');
        modalVisible.value = false;
        loadSteps();
      } catch (error) {
        console.error('更新失败:', error);
        message.error('更新失败');
      } finally {
        pendingSubmitData.value = null;
      }
    };

    // 取消版本变更（同时取消保存）
    const handleVersionChangeCancel = () => {
      pendingSubmitData.value = null;
      message.info('已取消保存');
    };

    // 重置表单
    const resetForm = () => {
      Object.assign(formData, {
        level: 1,
        parent_id: null,
        step_name: '',
        step_code: '',
        step_order: 0,
        description: '',
        remark: '',
        input_params: '',
        output_params: '',
        task_model_class: 'light',
        model_id: null,
        prompt_template_id: null,
        execution_condition: '',
        can_retry: false,
        is_enabled: 1,
        is_skippable_on_failure: 0
      });
      currentId.value = null;
    };

    // 编辑步骤匹配规则
    const handleEditStepMatchRules = (record) => {
      if (record.level !== 1) {
        message.warning('只能编辑步骤的匹配规则');
        return;
      }
      Object.assign(matchRulesForm, {
        scheme_step_id: record.scheme_step_id || record.id,
        step_name: record.step_name,
        step_order: record.step_order || 0,
        match_uid: record.match_uid || '',
        match_uid_suffix: record.match_uid_suffix || '',
        priority: record.priority !== undefined ? record.priority : 0,
        is_default: record.is_default || 0
      });
      currentEditStepId.value = record.scheme_step_id || record.id;
      editMatchRulesModalVisible.value = true;
    };

    // 更新匹配规则
    const handleUpdateMatchRules = async () => {
      try {
        if (!currentEditStepId.value) {
          message.error('步骤ID不存在');
          return;
        }

        // 如果设置为默认步骤，需要调用 setDefaultStep API
        if (matchRulesForm.is_default === 1) {
          await setDefaultStep(currentEditStepId.value);
          message.success('设置默认步骤成功');
        }

        // 更新匹配规则
        await updateSchemeStep(currentEditStepId.value, {
          step_order: matchRulesForm.step_order,
          is_required: 1, // 保持原有值
          remark: '',
          match_uid: matchRulesForm.match_uid || null,
          match_uid_suffix: matchRulesForm.match_uid_suffix || null,
          priority: matchRulesForm.priority || 0,
          is_default: matchRulesForm.is_default === 1 ? 1 : 0
        });

        message.success('更新匹配规则成功');
        editMatchRulesModalVisible.value = false;
        await loadSteps();
      } catch (error) {
        console.error('更新匹配规则失败:', error);
        message.error('更新匹配规则失败: ' + (error.response?.data?.message || error.message));
      }
    };

    // 设置默认步骤
    const handleSetDefaultStep = async (record) => {
      if (record.level !== 1) {
        message.warning('只能设置步骤为默认');
        return;
      }
      const stepId = record.scheme_step_id || record.id;
      if (!stepId) {
        message.error('步骤ID不存在');
        return;
      }
      try {
        await setDefaultStep(stepId);
        message.success('设置默认步骤成功');
        await loadSteps();
      } catch (error) {
        console.error('设置默认步骤失败:', error);
        message.error('设置默认步骤失败: ' + (error.response?.data?.message || error.message));
      }
    };

    // 获取 Worker 的步骤序号（X.Y 格式）
    const getWorkerStepNumber = (record) => {
      if (record.level !== 2) return '';
      // 找到父步骤
      const parentStep = dataSource.value.find(item => item.level === 1 && item.id === record.parent_id);
      const parentOrder = parentStep ? parentStep.step_order : 0;
      const workerOrder = record.step_order || 0;
      return `${parentOrder}.${workerOrder}`;
    };

    // 开始编辑 Worker 序号
    const startEditWorkerOrder = (record) => {
      editingWorkerId.value = record.id;
      editingWorkerOrder.value = record.step_order || 0;
      // 延迟聚焦，等待 input 渲染
      setTimeout(() => {
        const input = document.querySelector('.ant-input-number-input');
        if (input) {
          input.focus();
          input.select();
        }
      }, 50);
    };

    // 保存 Worker 序号
    const saveWorkerOrder = async (record) => {
      if (editingWorkerId.value !== record.id) return;
      
      const newOrder = editingWorkerOrder.value;
      const oldOrder = record.step_order || 0;
      
      // 如果没有变化，直接取消编辑
      if (newOrder === oldOrder) {
        cancelEditWorkerOrder();
        return;
      }

      try {
        await updateCallStep(record.id, {
          step_order: newOrder
        });
        message.success('序号更新成功');
        // 更新本地数据
        record.step_order = newOrder;
        cancelEditWorkerOrder();
        // 重新加载列表以确保排序正确
        await loadSteps();
      } catch (error) {
        console.error('更新序号失败:', error);
        message.error('更新序号失败: ' + (error.response?.data?.message || error.message));
        cancelEditWorkerOrder();
      }
    };

    // 取消编辑 Worker 序号
    const cancelEditWorkerOrder = () => {
      editingWorkerId.value = null;
      editingWorkerOrder.value = 0;
    };

    // 切换Worker启用状态
    const handleToggleEnabled = async (record, checked) => {
      if (record.level !== 2) {
        return;
      }
      
      const workerId = record.id;
      if (!workerId) {
        message.error('Worker ID不存在');
        // 恢复原值
        record.is_enabled = record.is_enabled === 1 ? 0 : 1;
        return;
      }

      // 保存原值，用于失败时恢复
      const oldValue = record.is_enabled !== undefined ? record.is_enabled : 1;
      // 设置更新状态，防止重复点击
      record._updatingEnabled = true;
      // checked 是 checked-value 或 unchecked-value 的值，即 1 或 0
      const newValue = checked === 1 || checked === true ? 1 : 0;

      try {
        await updateCallStep(workerId, {
          is_enabled: newValue
        });
        // v-model 已经自动更新了 record.is_enabled，这里只需要确认
        message.success(newValue === 1 ? '已启用' : '已禁用');
      } catch (error) {
        console.error('切换启用状态失败:', error);
        message.error('切换启用状态失败: ' + (error.response?.data?.message || error.message));
        // 如果失败，恢复原值
        record.is_enabled = oldValue;
      } finally {
        record._updatingEnabled = false;
      }
    };

    // 监听 schemeId 变化，重新加载数据
    watch(schemeId, (newId, oldId) => {
      if (newId && newId !== oldId) {
        console.log('schemeId 变化，重新加载数据:', oldId, '->', newId);
        loadScheme();
        loadSteps();
      }
    }, { immediate: false });

    // 响应式屏幕监听
    const handleResize = () => {
      screenWidth.value = window.innerWidth;
    };

    onMounted(() => {
      loadScheme();
      loadSteps();
      // 模型和提示词列表改为按需加载，只在打开Worker编辑弹窗时加载
      
      // 添加窗口大小监听
      window.addEventListener('resize', handleResize);
    });

    onUnmounted(() => {
      // 移除窗口大小监听
      window.removeEventListener('resize', handleResize);
    });

    return {
      schemeId,
      schemeName,
      loading,
      dataSource,
      modalVisible,
      isEdit,
      formData,
      columns,
      modelList,
      promptList,
      promptTemplateList,
      parentSteps,
      workerKnowledgeMap,
      workerPromptTemplateMap,
      expandedWorkers,
      expandedRowKeys,
      associateModalVisible,
      setPromptTemplateModalVisible,
      selectedPromptTemplateId,
      knowledgeList,
      knowledgeLoading,
      selectedKnowledgeList,
      associatePagination,
      previewModalVisible,
      previewData,
      associateForm,
      knowledgeColumns,
      filterPromptTemplateOption,
      getKnowledgeTypeColor,
      getKnowledgeTypeName,
      isKnowledgeSelected,
      handleKnowledgeSelect,
      handleKnowledgeSelectAll,
      removeSelectedKnowledge,
      updateSortOrder,
      handleAssociateKnowledge,
      handleAssociateSubmit,
      handlePreviewTemplate,
      loadKnowledgeList,
      handleKnowledgeTableChange,
      handleEditKnowledge,
      handleExpand,
      handleSetPromptTemplate,
      handleSetPromptTemplateFromModal,
      handleSetPromptTemplateSubmit,
      handleBack,
      handleViewOutputParams,
      handleCreateStep,
      handleCreateWorker,
      handleAddWorker,
      handleEdit,
      handleDelete,
      handleSubmit,
      handleCancel,
      versionChangeModalVisible,
      schemeVersion,
      handleVersionChangeConfirm,
      handleVersionChangeSkip,
      handleVersionChangeCancel,
      editMatchRulesModalVisible,
      matchRulesForm,
      handleEditStepMatchRules,
      handleUpdateMatchRules,
      handleSetDefaultStep,
      handleToggleEnabled,
      isAllExpanded,
      handleToggleExpandAll,
      screenWidth,
      breakpoints,
      // Worker 序号编辑相关
      editingWorkerId,
      editingWorkerOrder,
      getWorkerStepNumber,
      startEditWorkerOrder,
      saveWorkerOrder,
      cancelEditWorkerOrder
    };
  }
};
</script>

<style scoped>
/* 隐藏 Ant Design Vue 自动生成的展开列（第一列），设置宽度为0 */
:deep(.ant-table-thead > tr > th:first-child),
:deep(.ant-table-tbody > tr > td:first-child) {
  width: 0 !important;
  min-width: 0 !important;
  max-width: 0 !important;
  padding: 0 !important;
  border: none !important;
  overflow: hidden;
}

/* 响应式优化 - 小屏幕下减少内边距 */
@media (max-width: 768px) {
  :deep(.ant-table-cell) {
    padding: 8px 4px !important;
    font-size: 13px;
  }
  
  :deep(.ant-btn-link) {
    padding: 0 4px;
  }
  
  :deep(.ant-tag) {
    font-size: 12px;
    padding: 0 4px;
  }
}

/* 中等屏幕优化 */
@media (min-width: 768px) and (max-width: 1024px) {
  :deep(.ant-table-cell) {
    padding: 12px 8px !important;
  }
}

/* 表格横向滚动优化 */
:deep(.ant-table-wrapper) {
  overflow-x: auto;
}

/* 确保固定列在滚动时正常显示 */
:deep(.ant-table-cell-fix-right) {
  background-color: #fff;
}

/* 匹配规则内容优化 - 垂直排列 */
:deep(.ant-table-cell) .match-rules-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
</style>

